/**
 * SourceApplicationPage
 * Main page for managing source generator applications across organizational levels
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  Chip,
  Stack,
  ListItemText,
} from '@mui/material';
import {
  Business as CompanyIcon,
  Domain as DepartmentIcon,
  Work as OccupationIcon,
  Person as EmployeeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { PageShell } from '../../components';
import { DualTransferList, SourceApplicationModal, SourceExposureLevelBadge } from '../../components/source-applications';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { occupationService } from '../../services/occupationService';
import { employeeService } from '../../services/employeeService';
import {
  ApplicationLevel,
  ApplicationLevelLabels,
  SourceApplicationData,
  SourceWithApplication,
  AvailableSource,
  SourceExposureLevel,
  isSourceWithApplication,
  RiskTypeLabels,
} from '../../types/sourceApplication';
import { useSourceApplications } from './hooks/useSourceApplications';

// ============================================================================
// TYPES
// ============================================================================

interface SelectOption {
  id: number;
  name: string;
  parentId?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement | null {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const levelIcons: Record<ApplicationLevel, React.ReactElement> = {
  [ApplicationLevel.COMPANY]: <CompanyIcon />,
  [ApplicationLevel.DEPARTMENT]: <DepartmentIcon />,
  [ApplicationLevel.OCCUPATION]: <OccupationIcon />,
  [ApplicationLevel.EMPLOYEE]: <EmployeeIcon />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const SourceApplicationPage: React.FC = () => {
  // Source application state from hook
  const {
    applied,
    available,
    alerts,
    loading,
    loadingAlerts,
    error,
    alertsError,
    loadSources,
    loadAlerts,
    applyMultipleSources,
    removeMultipleSources,
    updateApplication,
    clearError,
    clearAlertsError,
  } = useSourceApplications();

  // Local UI state
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLevel, setSelectedLevel] = useState<ApplicationLevel>(
    ApplicationLevel.COMPANY
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [selectedOccupationId, setSelectedOccupationId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [highlightField, setHighlightField] = useState<string | null>(null);

  // Options for selectors
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [departments, setDepartments] = useState<SelectOption[]>([]);
  const [occupations, setOccupations] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'apply' | 'edit'>('apply');
  const [selectedSource, setSelectedSource] = useState<
    AvailableSource | SourceWithApplication | null
  >(null);
  const [pendingApplySources, setPendingApplySources] = useState<AvailableSource[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================================
  // LOAD OPTIONS
  // ============================================================================

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingOptions(true);
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(
          response.data.map((c) => ({
            id: Number(c.id),
            name: c.tradeName || c.legalName,
          }))
        );
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadCompanies();
  }, []);

  // Load departments when company changes
  useEffect(() => {
    if (!selectedCompanyId) {
      setDepartments([]);
      return;
    }

    const loadDepartments = async () => {
      try {
        const response = await departmentService.getDepartments({
          companyId: String(selectedCompanyId),
          limit: 1000,
        });
        setDepartments(
          response.data.map((d) => ({
            id: Number(d.id),
            name: d.name,
            parentId: Number(d.companyId),
          }))
        );
      } catch (err) {
        console.error('Error loading departments:', err);
      }
    };
    loadDepartments();
  }, [selectedCompanyId]);

  // Load occupations when department changes
  useEffect(() => {
    if (!selectedDepartmentId) {
      setOccupations([]);
      return;
    }

    const loadOccupations = async () => {
      try {
        const response = await occupationService.getOccupations({
          limit: 1000,
          filters: { departmentId: selectedDepartmentId },
        });
        setOccupations(
          response.data.map((o) => ({
            id: o.id,
            name: o.name,
            parentId: o.departmentId,
          }))
        );
      } catch (err) {
        console.error('Error loading occupations:', err);
      }
    };
    loadOccupations();
  }, [selectedDepartmentId]);

  // Load employees when occupation changes
  useEffect(() => {
    if (!selectedOccupationId) {
      setEmployees([]);
      return;
    }

    const loadEmployees = async () => {
      try {
        const response = await employeeService.getEmployees({
          limit: 1000,
          filters: { occupationId: selectedOccupationId },
        });
        setEmployees(
          response.data.map((e) => ({
            id: e.id,
            name: e.name,
            parentId: e.occupationId,
          }))
        );
      } catch (err) {
        console.error('Error loading employees:', err);
      }
    };
    loadEmployees();
  }, [selectedOccupationId]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const currentTargetId = useMemo((): number | null => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return selectedCompanyId;
      case ApplicationLevel.DEPARTMENT:
        return selectedDepartmentId;
      case ApplicationLevel.OCCUPATION:
        return selectedOccupationId;
      case ApplicationLevel.EMPLOYEE:
        return selectedEmployeeId;
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
  ]);

  const currentTargetName = useMemo((): string => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return companies.find((c) => c.id === selectedCompanyId)?.name || '';
      case ApplicationLevel.DEPARTMENT:
        return departments.find((d) => d.id === selectedDepartmentId)?.name || '';
      case ApplicationLevel.OCCUPATION:
        return occupations.find((o) => o.id === selectedOccupationId)?.name || '';
      case ApplicationLevel.EMPLOYEE:
        return employees.find((e) => e.id === selectedEmployeeId)?.name || '';
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
    companies,
    departments,
    occupations,
    employees,
  ]);

  const getEmptyStateMessage = (): string => {
    if (!selectedCompanyId) return 'Selecione uma empresa para gerenciar as fontes geradoras.';
    const missing: string[] = [];
    if (selectedLevel !== ApplicationLevel.COMPANY && !selectedDepartmentId) missing.push('um setor');
    if ((selectedLevel === ApplicationLevel.OCCUPATION || selectedLevel === ApplicationLevel.EMPLOYEE) && !selectedOccupationId) missing.push('um cargo');
    if (selectedLevel === ApplicationLevel.EMPLOYEE && !selectedEmployeeId) missing.push('um funcionário');
    if (missing.length === 0) return 'Selecione um destino para gerenciar as fontes geradoras.';
    return `Selecione ${missing.join(', ')} para gerenciar as fontes geradoras.`;
  };

  const alertsCounts = useMemo(() => {
    if (!alerts) return { critical: 0, veryHigh: 0, high: 0 };
    return {
      critical: alerts.summary?.totalCritical || 0,
      veryHigh: alerts.summary?.veryHighCount || 0,
      high: alerts.summary?.highCount || 0,
    };
  }, [alerts]);

  // ============================================================================
  // LOAD SOURCES WHEN TARGET CHANGES
  // ============================================================================

  useEffect(() => {
    if (currentTargetId) {
      loadSources(selectedLevel, currentTargetId);
    }
  }, [selectedLevel, currentTargetId, loadSources]);

  // Load alerts when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadAlerts(selectedCompanyId);
    }
  }, [selectedCompanyId, loadAlerts]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const pulseAnimation = {
    '@keyframes borderPulse': {
      '0%': { borderColor: 'error.main', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
      '50%': { borderColor: 'error.main', boxShadow: '0 0 0 4px rgba(211, 47, 47, 0.1)' },
      '100%': { borderColor: 'divider', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      animation: 'borderPulse 1.5s ease-in-out infinite',
    },
  };

  const triggerHighlight = (field: string) => {
    setHighlightField(field);
  };

  const handleLevelChange = (level: ApplicationLevel) => {
    setSelectedLevel(level);
    // Reset dependent selections and highlight the next field to fill
    if (level === ApplicationLevel.COMPANY) {
      setSelectedDepartmentId(null);
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.DEPARTMENT) {
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
      triggerHighlight('department');
    } else if (level === ApplicationLevel.OCCUPATION) {
      setSelectedEmployeeId(null);
      if (!selectedDepartmentId) {
        triggerHighlight('department');
      } else {
        triggerHighlight('occupation');
      }
    } else if (level === ApplicationLevel.EMPLOYEE) {
      if (!selectedDepartmentId) {
        triggerHighlight('department');
      } else if (!selectedOccupationId) {
        triggerHighlight('occupation');
      } else {
        triggerHighlight('employee');
      }
    }
  };

  const handleApply = useCallback(
    async (items: (AvailableSource | SourceWithApplication)[]) => {
      const availableItems = items.filter(
        (item): item is AvailableSource => !isSourceWithApplication(item)
      );

      if (availableItems.length === 0) return;

      // Store pending sources and open modal for configuration
      setPendingApplySources(availableItems);
      setSelectedSource(availableItems[0]);
      setModalMode('apply');
      setModalOpen(true);
    },
    []
  );

  const handleRemove = useCallback(
    async (items: (AvailableSource | SourceWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is SourceWithApplication => isSourceWithApplication(item)
      );

      if (appliedItems.length === 0) return;

      const sourceGeneratorIds = appliedItems.map((item) => item.sourceGeneratorId);
      const success = await removeMultipleSources(selectedLevel, currentTargetId, sourceGeneratorIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${sourceGeneratorIds.length} fonte(s) geradora(s) removida(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleSources]
  );

  const handleModalConfirm = useCallback(
    async (data: SourceApplicationData) => {
      if (!currentTargetId || pendingApplySources.length === 0) return;

      const sourceGeneratorIds = pendingApplySources.map((source) => source.id);
      const success = await applyMultipleSources(
        selectedLevel,
        currentTargetId,
        sourceGeneratorIds,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: `${sourceGeneratorIds.length} fonte(s) geradora(s) aplicada(s) com sucesso`,
          severity: 'success',
        });
        setModalOpen(false);
        setPendingApplySources([]);
        setSelectedSource(null);
      }
    },
    [currentTargetId, selectedLevel, pendingApplySources, applyMultipleSources]
  );

  const handleEditConfirm = useCallback(
    async (data: SourceApplicationData) => {
      if (!currentTargetId || !selectedSource || !isSourceWithApplication(selectedSource)) return;

      const success = await updateApplication(
        selectedLevel,
        currentTargetId,
        selectedSource.sourceGeneratorId,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: 'Aplicação atualizada com sucesso',
          severity: 'success',
        });
        setModalOpen(false);
        setSelectedSource(null);
      }
    },
    [currentTargetId, selectedLevel, selectedSource, updateApplication]
  );

  const handleItemEdit = useCallback(
    (item: AvailableSource | SourceWithApplication) => {
      if (!isSourceWithApplication(item)) return;

      setSelectedSource(item);
      setModalMode('edit');
      setModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setPendingApplySources([]);
    setSelectedSource(null);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderLevelSelector = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Selecionar Nível e Destino" />
      <CardContent>
        <Grid container spacing={3}>
          {/* Level Selector */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Nível</InputLabel>
              <Select
                value={selectedLevel}
                label="Nível"
                onChange={(e) => handleLevelChange(e.target.value as ApplicationLevel)}
              >
                {Object.values(ApplicationLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {levelIcons[level]}
                      <span>{ApplicationLevelLabels[level]}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Company Selector (always shown) */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompanyId || ''}
                label="Empresa"
                onChange={(e) => {
                  setSelectedCompanyId(Number(e.target.value));
                  setSelectedDepartmentId(null);
                  setSelectedOccupationId(null);
                  setSelectedEmployeeId(null);
                }}
                disabled={loadingOptions}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Department Selector (shown for department, occupation, employee) */}
          {selectedLevel !== ApplicationLevel.COMPANY && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth sx={highlightField === 'department' ? pulseAnimation : undefined}>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={selectedDepartmentId || ''}
                  label="Setor"
                  onChange={(e) => {
                    setSelectedDepartmentId(Number(e.target.value));
                    setSelectedOccupationId(null);
                    setSelectedEmployeeId(null);
                    if (selectedLevel === ApplicationLevel.OCCUPATION || selectedLevel === ApplicationLevel.EMPLOYEE) {
                      triggerHighlight('occupation');
                    } else {
                      setHighlightField(null);
                    }
                  }}
                  disabled={!selectedCompanyId || departments.length === 0}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Occupation Selector (shown for occupation, employee) */}
          {(selectedLevel === ApplicationLevel.OCCUPATION ||
            selectedLevel === ApplicationLevel.EMPLOYEE) && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth sx={highlightField === 'occupation' ? pulseAnimation : undefined}>
                <InputLabel>Cargo</InputLabel>
                <Select
                  value={selectedOccupationId || ''}
                  label="Cargo"
                  onChange={(e) => {
                    setSelectedOccupationId(Number(e.target.value));
                    setSelectedEmployeeId(null);
                    if (selectedLevel === ApplicationLevel.EMPLOYEE) {
                      triggerHighlight('employee');
                    } else {
                      setHighlightField(null);
                    }
                  }}
                  disabled={!selectedDepartmentId || occupations.length === 0}
                >
                  {occupations.map((occ) => (
                    <MenuItem key={occ.id} value={occ.id}>
                      {occ.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Employee Selector (shown for employee only) */}
          {selectedLevel === ApplicationLevel.EMPLOYEE && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth sx={highlightField === 'employee' ? pulseAnimation : undefined}>
                <InputLabel>Funcionário</InputLabel>
                <Select
                  value={selectedEmployeeId || ''}
                  label="Funcionário"
                  onChange={(e) => {
                    setSelectedEmployeeId(Number(e.target.value));
                    setHighlightField(null);
                  }}
                  disabled={!selectedOccupationId || employees.length === 0}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Current Target Info */}
        {currentTargetId && currentTargetName && (
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={levelIcons[selectedLevel]}
              label={`${ApplicationLevelLabels[selectedLevel]}: ${currentTargetName}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderSourceTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Fontes Geradoras"
        subheader={
          currentTargetId
            ? `Aplicar ou remover fontes geradoras de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar fontes geradoras'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">{getEmptyStateMessage()}</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={available}
            applied={applied}
            onApply={handleApply}
            onRemove={handleRemove}
            getItemKey={(item) => (isSourceWithApplication(item) ? item.sourceGeneratorId : item.id)}
            getItemLabel={(item) => (isSourceWithApplication(item) ? item.sourceName : item.name)}
            getItemSecondary={(item) => RiskTypeLabels[item.riskType] || item.riskType}
            searchFields={['riskType', 'location']}
            availableTitle="Fontes Disponíveis"
            appliedTitle="Fontes Aplicadas"
            loading={loading}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={isSourceWithApplication(item) ? item.sourceName : item.name}
                secondary={
                  isApplied && isSourceWithApplication(item) ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <SourceExposureLevelBadge
                        level={item.exposureLevel}
                        size="small"
                        exposureDays={item.exposureDays}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {RiskTypeLabels[item.riskType] || item.riskType}
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={RiskTypeLabels[item.riskType] || item.riskType} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {isSourceWithApplication(item) ? item.location : item.location}
                      </Typography>
                    </Stack>
                  )
                }
              />
            )}
            onItemEdit={handleItemEdit}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderAlertsTab = () => (
    <Card>
      <CardHeader
        title="Alertas de Exposição Crítica"
        subheader="Fontes geradoras com nível de exposição alto ou muito alto"
      />
      <CardContent>
        {!selectedCompanyId ? (
          <Alert severity="info">
            Selecione uma empresa para visualizar os alertas de exposição crítica.
          </Alert>
        ) : loadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : alertsError ? (
          <Alert severity="error" onClose={clearAlertsError}>
            {alertsError}
          </Alert>
        ) : !alerts || alerts.critical.length === 0 ? (
          <Alert severity="success">
            Nenhuma fonte geradora com nível de exposição crítico encontrada.
          </Alert>
        ) : (
          <Box>
            {/* Summary */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Chip
                label={`Total: ${alertsCounts.critical}`}
                color="default"
              />
              <Chip
                label={`Muito Alto: ${alertsCounts.veryHigh}`}
                color="error"
              />
              <Chip
                label={`Alto: ${alertsCounts.high}`}
                color="warning"
              />
            </Box>

            {/* Critical Alerts List */}
            {alerts.critical.map((alert) => (
              <Card
                key={`critical-${alert.applicationId}`}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderColor:
                    alert.exposureLevel === SourceExposureLevel.VERY_HIGH
                      ? 'error.main'
                      : 'warning.main',
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="subtitle2">{alert.sourceName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                      </Typography>
                    </Box>
                    <SourceExposureLevelBadge
                      level={alert.exposureLevel}
                      exposureDays={alert.exposureDays}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Segurança', path: '/seguranca' },
        { label: 'Aplicação de Fontes Geradoras' },
      ]}
      title="Aplicação de Fontes Geradoras"
      subtitle="Gerencie a aplicação de fontes geradoras em empresas, setores, cargos e funcionários"
    >
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Level Selector */}
      {renderLevelSelector()}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Gerenciar Fontes" />
          <Tab
            label={
              <Badge
                badgeContent={alertsCounts.critical}
                color="error"
                max={99}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  Alertas Críticos
                </Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {renderSourceTransferList()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderAlertsTab()}
      </TabPanel>

      {/* Modal */}
      <SourceApplicationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={modalMode === 'apply' ? handleModalConfirm : handleEditConfirm}
        source={selectedSource}
        mode={modalMode}
        loading={loading}
        error={error}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default SourceApplicationPage;
